import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import {
  CheckoutCartMutation,
  CheckoutCartMutationVariables,
  CollectionFeeDetailFragmentDoc,
  FeeDetailFragmentDoc,
  PaymentDetailFragmentDoc,
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  StoreItemPurchaseConfirmationQuery,
  StoreItemPurchaseConfirmationQueryVariables,
  UserAddressFragmentDoc,
  UserCartFragment,
  UserCartStoreItemPurchaseConfirmationFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config, constants } from '@/config'
import { useCurrentUser, useMessage, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { makeViewModel } from '@/core/architecture'
import {
  addCollectionToCollectionFee,
  groupItemsByCollection,
} from '@/core/cart'
import { App } from '@/core/native'
import { Device } from '@/core/native'
import {
  getStoreProceed,
  removeUserInfoRegisterRedirect,
} from '@/core/native/storage'
import { useRouter } from '@/core/router'
import { convertUserCartItemsToSku } from '@/core/sku'
import { generateTransactionId } from '@/lib/analytics'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showError, handleClose } = useMessage()
  const { analytics } = useAnalytics()
  const { user } = useCurrentUser()
  const [{ fullName, purchaseType, pickupTimes }, setStoreProceed] = useState<
    PromiseType<ReturnType<typeof getStoreProceed>>
  >({})
  const { t } = useTranslation('storeItemPurchaseProceed')
  const { router, generatePath, routerPages } = useRouter<{
    cartId: string
  }>()
  const cartId = router.query.cartId
  const { deviceInfo } = Device.useDeviceInfo()

  const [getStoreItemPurchase, { data, loading }] = useLazyQuery<
    StoreItemPurchaseConfirmationQuery,
    StoreItemPurchaseConfirmationQueryVariables
  >(QUERY, {
    variables: {
      cartId,
      isKonbini: purchaseType === 'konbini',
    },
  })

  const [sendVerificationSMS, { loading: sendingVerification }] = useMutation<
    SendVerificationSmsMutation,
    SendVerificationSmsMutationVariables
  >(SendVerificationSms)

  useEffect(() => {
    removeUserInfoRegisterRedirect()
    ;(async () => {
      const value = await getStoreProceed()
      setStoreProceed(value)
      await getStoreItemPurchase({
        variables: {
          cartId,
          isKonbini: value.purchaseType === 'konbini',
        },
      })
    })()
  }, [])

  /* チェックアウト */
  const [checkoutCart, { loading: checkingOut }] = useMutation<
    CheckoutCartMutation,
    CheckoutCartMutationVariables
  >(CHECKOUT_CART)

  const cartItems = convertUserCartItemsToSku(
    data?.userCart?.items ?? null
  ) as UserCartFragment['items']

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  return {
    state: {
      cart: data?.userCart,
      cartItems,
      cartItemsByCollection: groupItemsByCollection(cartItems),
      events:
        data?.userCart?.items
          .map(({ item }) => item.collection?.event)
          .filter(
            (_event, i, self) => self.indexOf(_event) === i && !!_event
          ) ?? [],
      fullName: fullName ?? (data?.user?.fullName || ''),
      paymentSource: data?.paymentSource,
      address: data?.user?.address ?? null,
      phoneNumber: data?.user?.phoneNumber ?? '',
      isPhoneNumberVerified: !!data?.user?.isPhoneNumberVerified,
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      isCardRegistered: !!data?.paymentSource?.last4,
      purchaseType,
      loading,
      submitting: checkingOut,
      pickupTimes: pickupTimes ?? null,
      systemFee: addCollectionToCollectionFee(
        data?.userCart?.checkoutFees?.collections,
        data?.userCart?.items.map(({ item }) => item.collection)
      ),
      shippingFee: data?.userCart?.checkoutFees?.shipping ?? null,
      konbiniFee: data?.userCart?.checkoutFees?.konbini ?? null,
      sendingVerification,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
    },
    actions: {
      async onCheckout() {
        const purchaseTypeAnalytics =
          purchaseType === 'konbini'
            ? 'convenience_store'
            : purchaseType === 'cash'
            ? 'credit_card'
            : 'prepaid_point'

        await analytics?.track('begin_checkout', {
          item_list_id: `artist_store_list_${data?.userCart?.artist.id}`,
          item_list_name: 'Artist Store Items',
          type: purchaseTypeAnalytics,
          value: data?.userCart?.totalPrice,
          items: cartItems.map(cartItem => ({
            item_id: cartItem.item.id,
            item_name: cartItem.item.title,
            item_category: ConversionCategories.storeItem,
            item_category2: cartItem.item.type,
            item_category3: App.getPlatform(),
            item_category4: cartItem.item.sku?.id,
            item_category5: data?.userCart?.artist.id,
            price: cartItem.totalPrice,
            quantity: cartItem.count,
          })),
        })

        if (!data?.userCart) return

        const isKonbini = purchaseType === 'konbini'
        const inputs = isKonbini
          ? {
              cartId,
              totalPrice:
                data.userCart.totalPrice +
                (data.userCart.checkoutFees?.totalFee ?? 0),
              isKonbini,
              fullName,
              pickupTimes,
            }
          : {
              cartId,
              totalPrice:
                data.userCart.totalPrice +
                (data.userCart.checkoutFees?.totalFee ?? 0),
              isKonbini,
              pickupTimes,
            }

        return checkoutCart({
          variables: {
            inputs,
          },
          context: {
            headers: { 'x-utoniq-idempotency-key': createUuid() },
          },
        })
          .then(({ data: _data }) => {
            analytics?.ecommerce({
              transaction_id: generateTransactionId(user?.uid ?? ''),
              currency: 'JPY',
              type: purchaseTypeAnalytics,
              value: data?.userCart?.totalPrice,
              items: cartItems.map(cartItem => ({
                item_category: ConversionCategories.storeItem,
                item_category2: 'cart',
                item_category3: deviceInfo?.platform,
                item_category4: 'StoreItemPurchaseConfirmation',
                item_category5: data.userCart?.artist.id,
                price: cartItem.totalPrice ?? 0,
                quantity: cartItem.count,
                item_id: cartItem.item.id,
                item_name: cartItem.item.title,
              })),
            })

            if (_data?.checkoutCart?.id) {
              router.replace(
                generatePath(routerPages.StoreItemPurchaseComplete, {
                  cartTransactionOrderId: _data?.checkoutCart?.id,
                })
              )
            }
          })
          .catch(error => {
            analytics?.track('error_cart_checkout', {
              applicationKey: config.applicationKey,
              artistId: data?.userCart?.artist.id,
              error: error.name,
              errorCode: error.graphQLErrors[0].extensions?.errorCode ?? '',
            })

            if (
              error?.graphQLErrors?.[0]?.extensions?.exception?.errors?.some(
                (err: { errorCode?: string }) =>
                  err?.errorCode === 'ITEM_PURCHASE_LIMIT'
              )
            ) {
              showError(
                new Error(t('itemPurchaseLimitErrorMessage')),
                undefined,
                undefined,
                undefined,
                <Typography
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    handleClose()
                    router.goBack(
                      generatePath(routerPages.StoreItemPurchaseProceed, {
                        cartId,
                      })
                    )
                  }}
                  variant="subtitle1"
                  color="white"
                >
                  {t('changeQuantity')}
                </Typography>
              )
              return
            }
            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              case constants.errorCodes.NEED_PATRON:
                showError(new Error(t('needPatronErrorMessage')))
                return
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                return
              /**
               * @desciption
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                return
              /**
               * @desciption
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                return
              default:
                showError(error)
                break
            }
          })
      },
      async onSendVerificationSMS() {
        sendVerificationSMS()
          .then(() => {
            router.push(routerPages.SMSVerification)
          })
          .catch(e => {
            showError(e)
          })
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
    },
  }
})

const CHECKOUT_CART = gql`
  mutation checkoutCart($inputs: CheckoutCartInput!) {
    checkoutCart(inputs: $inputs) {
      id
      isKonbini
    }
  }
`

const QUERY = gql`
  query StoreItemPurchaseConfirmation($cartId: ID!, $isKonbini: Boolean) {
    userCart(id: $cartId) {
      ...UserCartStoreItemPurchaseConfirmation
      checkoutFees(isKonbini: $isKonbini) {
        totalFee
        shipping {
          ...FeeDetail
        }
        collections {
          ...CollectionFeeDetail
        }
        konbini {
          ...FeeDetail
        }
      }
    }
    paymentSource {
      ...PaymentDetail
    }
    user {
      id
      fullName
      phoneNumber
      formattedPhoneNumber @client
      isPhoneNumberVerified
      ...UserAddress
    }
    prepaidPointBalance {
      lastUpdatedAt
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
  }
  ${PaymentDetailFragmentDoc}
  ${UserAddressFragmentDoc}
  ${UserCartStoreItemPurchaseConfirmationFragmentDoc}
  ${FeeDetailFragmentDoc}
  ${CollectionFeeDetailFragmentDoc}
`
